import { SortableTableHead } from '@/components/ui/sortable-table-head'
import { useSortingState } from '@/hooks/useSortingState'
import { useListPagination } from '@/hooks/useListPagination'
import { ListPagination } from '@/components/ui/list-pagination'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getOrdiniArchiviati } from '@/api/ordini'
import { useRipristinaOrdine } from '@/hooks/useOrdini'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Archive, ArrowLeft, Search, RotateCcw } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

/**
 * Punto 17 feedback cliente: pagina che mostra gli ordini archiviati (soft-deleted)
 * e permette il ripristino.
 */
export default function OrdiniArchivio() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [ordering, setOrdering] = useSortingState('ordini-archivio', '-deleted_at')
  const [ordineDaRipristinare, setOrdineDaRipristinare] = useState(null)

  const { page, setPage } = useListPagination({ search, ordering })

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['ordini-archiviati', { page, search, ordering }],
    queryFn: () => getOrdiniArchiviati({ page, search: search || undefined, ordering }).then((r) => r.data),
  })

  const ordini = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : []

  const restoreMutation = useRipristinaOrdine()

  const handleRestore = () => {
    if (!ordineDaRipristinare) return
    const ordineId = ordineDaRipristinare.id
    restoreMutation.mutate(ordineId, {
      onSuccess: () => {
        setOrdineDaRipristinare(null)
        navigate(`/ordini/${ordineId}`)
      },
    })
  }

  const formatDate = (d) =>
    d ? new Date(d).toLocaleString('it-IT') : '-'

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Archive className="h-7 w-7" />
            Archivio ordini
          </h1>
          <p className="text-muted-foreground">
            Ordini archiviati (soft-delete). Possono essere ripristinati.
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate('/ordini')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Torna alla lista
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Elenco archiviati</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative max-w-md">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Cerca per numero ordine, cliente..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
            />
          </div>

          {isLoading ? (
            <div className="text-muted-foreground">Caricamento...</div>
          ) : ordini.length === 0 ? (
            <div className="text-muted-foreground">Nessun ordine archiviato.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead field="numero" currentOrdering={ordering} onSort={setOrdering}>Numero</SortableTableHead>
                  <SortableTableHead field="cliente_nome" currentOrdering={ordering} onSort={setOrdering}>Cliente</SortableTableHead>
                  <SortableTableHead field="stato" currentOrdering={ordering} onSort={setOrdering}>Stato</SortableTableHead>
                  <SortableTableHead field="deleted_at" currentOrdering={ordering} onSort={setOrdering}>Archiviato il</SortableTableHead>
                  <SortableTableHead field="deleted_by__username" currentOrdering={ordering} onSort={setOrdering}>Archiviato da</SortableTableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ordini.map((o) => (
                  <TableRow key={o.id}>
                    <TableCell className="font-mono">{o.numero}</TableCell>
                    <TableCell>
                      {o.cliente?.nome_completo || o.cliente_nome_completo || '-'}
                    </TableCell>
                    <TableCell>{o.stato}</TableCell>
                    <TableCell>{formatDate(o.deleted_at)}</TableCell>
                    <TableCell>{o.deleted_by_username || '-'}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setOrdineDaRipristinare(o)}
                        disabled={restoreMutation.isPending}
                      >
                        <RotateCcw className="h-4 w-4 mr-1" />
                        Ripristina
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <AlertDialog
        open={Boolean(ordineDaRipristinare)}
        onOpenChange={(open) => !open && setOrdineDaRipristinare(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ripristina ordine</AlertDialogTitle>
            <AlertDialogDescription>
              {ordineDaRipristinare?.stato === 'annullato' ? (
                <>
                  L&apos;ordine {ordineDaRipristinare?.numero} tornerà allo stato{' '}
                  <strong>
                    {(ordineDaRipristinare?.stato_pre_annullamento || '').replace(/_/g, ' ')}
                  </strong> e verranno ricostruite disponibilità e riserve.
                  Se lo stock bloccante non è sufficiente, non verrà modificato nulla.
                </>
              ) : (
                <>
                  L&apos;ordine {ordineDaRipristinare?.numero} tornerà visibile nella lista ordini.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={restoreMutation.isPending}>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRestore}
              disabled={restoreMutation.isPending}
            >
              {restoreMutation.isPending ? 'Ripristino...' : 'Conferma ripristino'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <ListPagination data={data} page={page} setPage={setPage} isFetching={isFetching} error={error} />
    </div>
  )
}
